import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { Radio } from 'antd';
import { DropdownInput, Loader, ReportIcon } from 'modules/v2/common/components';
import { LabelInput, TextInput, Button } from 'modules/v2/common/AtomicDesign/atoms';
import { toTitleCase } from 'modules/v2/utils/stringManipulation';
import env from 'environments';
import { fallback3DCover } from 'modules/v2/pages/TemplatesBuilder/shared-components/SetupModal/assets';
import { FormData } from 'modules/v2/pages/TemplatesBuilder/utils/setupModalUtils';
import { DangerTriangleSolidIcon } from 'modules/v2/common/components/SvgIcon';

const isProduction = env.ENVIRONMENT === 'production';

interface Step3Props {
  bookData: any[],
  formData: FormData;
  onFormDataChange: (data: any) => void;
}

export const isStep3Valid = (formData: FormData) => {
  const hasValidWebsite = formData.website && (formData.website !== 'custom-link' || (formData.websiteCustom && validateURL(formData.websiteCustom)));
  return hasValidWebsite && formData.bookCover;
};

const validateURL = (str) => {
  const pattern = new RegExp('^(https?:\\/\\/)?'+ // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
    '(\\#[-a-z\\d_]*)?$','i'); // fragment locator
  return !!pattern.test(str);
};

export const Step3: React.FC<Step3Props> = ({ formData, bookData, onFormDataChange }) => {
  const [selectedBook, setSelectedBook] = useState(null);

  const [bookCoverIsLoading, setBookCoverIsLoading] = useState(false);
  const [selectedBook3DCover, setSelectedBook3DCover] = useState('');

  const handleInputChange = (field: string, value: string) => {
    onFormDataChange({ ...formData, [field]: value });
  };

  const handleWebsiteRadioChange = (e) => {
    handleInputChange('website', e.target.value);
  };

  const handleBookSelected = (book) => {
    setBookCoverIsLoading(true);
    setSelectedBook(book);

    formData.websiteCustom = '';
    formData.website = book?.links?.url?.site;

    handleInputChange('bookCover', book.thumbnail.url);
  };

  const mapAsBook = (option) => (
    <div
      className="flex items-center gap-4"
      key={option.name}
    >
      <img alt="book-cover" className='rounded-[5px]' src={option.thumbnail.url} width="33px" />

      <div className='flex flex-col'>
        <span>{toTitleCase(option.name)}</span>
        {/* {option.links.url.book && <span className="text-neutral-500">{option.links.url.book}</span>} */}
      </div>
    </div>
  );

  const getPublishedBooks = () => bookData.filter((currentBook) => !isEmpty(currentBook?.links?.url?.site));

  const getOptions = () => {
    const mapOption = (option, notAllowed = false) => ({
      id: option.name,
      key: option.name,
      value: option,
      label: notAllowed ? <span className="font-semibold text-neutral-500 text-sm uppercase">{option.name}</span> : mapAsBook(option),
      notAllowed
    });

    const currentOptions = [];

    const publishedBooks = getPublishedBooks();

    publishedBooks.forEach((book) => {
      // const foundCategory = currentOptions.find((option) => option.name === book?.book?.name);

      // if (!foundCategory)
      //   currentOptions.push(mapOption(book?.book, true));

      currentOptions.push(mapOption(book));
    });

    return currentOptions;
  };

  function getOriginFromUrl(url) {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.origin;
    } catch (error) {
      console.error('Invalid URL:', url);
      return null;
    }
  }

  function getFirstImageWith3dInSrc(doc) {
    const allImages = doc.querySelectorAll('img');

    for (const img of allImages) {
      if (img.src.includes('3d')) {
        return img.src;
      }
    }

    return null;
  }


  useEffect(() => {
    const fetch3dImage = async () => {
      try {
        const response = await fetch(selectedBook?.links?.url?.site);
        const htmlText = await response.text();

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        const desiredOrigin = getOriginFromUrl(selectedBook.links.url.site);
        const coverImageUrl = getFirstImageWith3dInSrc(doc);

        const cover3D = coverImageUrl.replace(window.location.origin, desiredOrigin);

        handleInputChange('bookCover3D', cover3D);
        setSelectedBook3DCover(cover3D);

        setTimeout(() => setBookCoverIsLoading(false), 200);
      } catch(err) {
        if (!isProduction) {
          handleInputChange('bookCover3D', selectedBook?.thumbnail?.url);
          setSelectedBook3DCover(fallback3DCover);

          setTimeout(() => setBookCoverIsLoading(false), 200);
        }

        console.error('[fetch3dImage]: Failed to fetch 3D book cover', err);
      }
    };

    if (selectedBook) fetch3dImage();
  }, [selectedBook]);

  useEffect(() => {
    if (formData.bookCover) {
      const publishedBooks = getPublishedBooks();

      const urlAreTheSame = (url1: string, url2: string) => {
        try {
          const firstUrl = new URL(url1);
          const secondUrl = new URL(url2);

          const samePathname = firstUrl.pathname === secondUrl.pathname;
          const sameOrigin = firstUrl.origin === secondUrl.origin;

          return samePathname && sameOrigin;
        } catch (err) {
          return false;
        }
      };

      const foundBook = publishedBooks.find((book) => urlAreTheSame(book?.thumbnail?.url, formData.bookCover));

      if (foundBook) {
        setSelectedBook(foundBook);

        if (!urlAreTheSame(formData?.website, foundBook?.links?.url?.site) && formData?.website !== 'custom-link') {
          formData.websiteCustom = formData.website;
          formData.website = 'custom-link';
        } else {
          formData.website = foundBook?.links?.url?.site;
        }
      }
    }

  }, [bookData]);

  const NoBooksAlert = (
    <div className="flex flex-col w-full h-full items-center font-figtree">
      <DangerTriangleSolidIcon width={35} height={35}/>

      <h2 className="text-red-500 mb-4">You Need to Customize Your First Book</h2>

      <span className="leading-5 text-neutral-800 mb-8">Looks like you haven't set up your book yet. Once you do, you'll be able to start customizing templates. Click below to get started.</span>
    </div>
  );

  if (!bookData || isEmpty(bookData)) {
    return NoBooksAlert;
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{
        __html: `
          .ant-radio-group .ant-radio-wrapper {
            width: 100%;
          }
          .ant-radio-checked .ant-radio-inner {
            accent-color: #06b6d4 !important;
            border-color: #06b6d4 !important;
          }
          .ant-radio:hover .ant-radio-inner {
            border-color: #06b6d4 !important;
          }
          .ant-radio-wrapper:hover .ant-radio-inner {
            border-color: #06b6d4 !important;
          }
        `
      }} />
      <div className="space-y-6">
          <div>
            <LabelInput required>Choose your Default Book</LabelInput>
            <DropdownInput
              placeholder="Select book"
              selectedValues={toTitleCase(selectedBook?.name)}
              onSelect={handleBookSelected}
              options={getOptions()}
              dropdownClasses='relative'
            />

            {selectedBook &&
            <>
              {bookCoverIsLoading && <Loader height="30vh" icon={<ReportIcon />} />}
              {!bookCoverIsLoading &&
                  <div className="flex mt-6 justify-start gap-36">
                    <div className="flex flex-col items-center">
                      <span className="text-sm leading-5 font-semibold">2D Book Cover Image</span>
                      <img className="mt-5 rounded-[5px]" alt="2D Book" src={selectedBook?.thumbnail?.url} width="145px" />
                    </div>

                    <div className="flex flex-col items-center">
                      <span className="text-sm leading-5 font-semibold">3D Book Cover Image</span>
                      <img alt="3D Book" src={selectedBook3DCover} width="170px" />
                    </div>
                  </div>
              }
            </>
            }
          </div>

          {selectedBook && <div>
            <LabelInput required>Your Website or Lead Capture Site Link</LabelInput>
            <div className="space-y-3 mt-2 max-w-full">
              <Radio.Group className="flex flex-col justify-between gap-2" onChange={handleWebsiteRadioChange} value={formData.website}>
                <div className={`flex items-center p-3 border rounded-md transition-colors hover:border-2 hover:border-cyan-500 hover:bg-cyan-50 ${formData.website === selectedBook?.links?.url?.site ? 'border-2 border-cyan-500 bg-cyan-50' : ''}`}>
                  <Radio value={selectedBook?.links?.url?.site} className="truncate mr-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-[#252525]">{selectedBook?.links?.url?.site}</span>
                    </div>
                  </Radio>
                </div>
                <div className={`flex items-center p-3 border rounded-md transition-colors hover:border-2 hover:border-cyan-500 hover:bg-cyan-50 ${formData.website === 'custom-link' ? 'border-2 border-cyan-500 bg-cyan-50' : ''}`}>
                  <Radio value="custom-link" className="mr-3">
                    <span className="text-sm font-medium text-[#252525]">Enter my own website</span>
                  </Radio>
                </div>
              </Radio.Group>
            </div>
          </div>}

          {Boolean(formData.website === 'custom-link') && <div className="mt-4">
            <LabelInput required>
              Your Website
            </LabelInput>
            <TextInput
              type="text"
              value={formData.websiteCustom}
              onChange={(e) => handleInputChange('websiteCustom', e.target.value)}
              isValid={formData.websiteCustom && validateURL(formData.websiteCustom)}
              errorMessage="Please enter a valid URL"
              placeholder="yourwebsite.com"
            />
          </div>}
      </div>
    </>
  );
};

